import React, { Fragment, useEffect, useMemo, useState, FC } from 'react';
import ModalDialog from '../../../../../@shared/modalDialog';
import { TGDPRPolicy, TGDPRPopupModal, TGDPRPopupModalPolicyFieldState } from '../../../../types/gdprConsentField.types';
import CheckboxSection from './checkboxSection';
import RadioSection from './radioSection';
import ContactSection from './contactSection';
import './styles.scss';

const GDPRPopupModal: FC<TGDPRPopupModal> = (props) => {
  const { handlePopupSubmit, gdprPolicies, hidePopup } = props;

  const [enableSubmit, setEnableSubmit] = useState(false);
  const [data, setData] = useState<TGDPRPopupModalPolicyFieldState>(() =>
    gdprPolicies.policies?.map((policy) => ({ id: policy.id, accepted: undefined, required: policy.required, type: policy.type }))
  );
  const contactChecboxes = useMemo(() => gdprPolicies.policies.filter((policy) => policy.type === 'checkbox_contacts'), [props]);
  const contactConsentDescriptionIndex = useMemo(
    () =>
      contactChecboxes.length > 0
        ? gdprPolicies.policies?.findIndex(
            (policy, i) => policy.type === 'checkbox' && gdprPolicies.policies[i + 1]?.type === 'checkbox_contacts'
          )
        : undefined,
    [props]
  );
  const isAllRadioDisagreed = !!data.every((item) =>
    item.type === 'radio' ? item.accepted === false || item.accepted === undefined : true
  );

  const isAnyRadioButtonNotAccepted = !!data.find((item) => item.type === 'radio' && item.accepted === undefined);

  const isAnyContactChecked = !!data.find((item) => item.type === 'checkbox_contacts' && item.accepted === true);

  useEffect(() => {
    setEnableSubmit(() => {
      const otherFiledsCheckStatus = data?.every((item, ind) => {
        if (item.type == 'checkbox_contacts' || ind == contactConsentDescriptionIndex) {
          return true;
        } else return item.required ? item.accepted : true;
      });
      // if atleast one accept radio is selected, checking alteast one Contact checboxes is selected
      return (
        otherFiledsCheckStatus &&
        (contactChecboxes?.length == 0 || isAllRadioDisagreed ? true : isAnyContactChecked) &&
        !isAnyRadioButtonNotAccepted
      );
    });
  }, [data]);

  const handleSubmit = () => {
    handlePopupSubmit(
      JSON.stringify({
        isFormOverlay: false,
        version: gdprPolicies.version,
        policies: data?.map(({ id, accepted, type }) =>
          type === 'checkbox_contacts' && isAllRadioDisagreed ? { id, accepted: false } : { id, accepted: accepted || false }
        ),
      })
    );
  };

  const updateData = (id: number, value: boolean) => {
    setData((data) =>
      data?.map((item) => {
        if (item.id === id) return { ...item, id, accepted: value };
        return item;
      })
    );
  };

  return (
    <ModalDialog
      isOpen={true}
      close={() => undefined}
      hideCloseButton={true}
      className="gdpr-popup-modal__content"
      overlayClassName="gdpr-popup-modal__overlay"
      skipModalPositionAdjustment={true}
    >
      <div className="gdpr-popup">
        {gdprPolicies.policies?.map((policy: TGDPRPolicy, index: number) => (
          <Fragment key={policy.id}>
            {policy.type === 'checkbox' && index !== contactConsentDescriptionIndex && (
              <CheckboxSection
                policy={policy}
                updateData={updateData}
                checked={data?.find((item) => item.id === policy.id)?.accepted || false}
              />
            )}
            {policy.type === 'radio' && (
              <RadioSection policy={policy} updateData={updateData} accepted={data?.find((item) => item.id === policy.id)?.accepted} />
            )}
          </Fragment>
        ))}
        <ContactSection
          contactChecboxes={contactChecboxes}
          updateData={updateData}
          textContent={contactConsentDescriptionIndex ? gdprPolicies.policies[contactConsentDescriptionIndex] : undefined}
          disabled={isAllRadioDisagreed}
          data={data}
        />
        <div className="gdpr-popup__button-wrapper">
          <button className="gdpr-popup__cancel-button" onClick={hidePopup}>
            {gdprPolicies.cancelButtonLabel}
          </button>

          <button
            className="gdpr-popup__submit-button"
            data-testid="gdpr-popup-submit-button"
            onClick={handleSubmit}
            disabled={!enableSubmit}
          >
            {gdprPolicies.submitButtonLabel}
          </button>
        </div>
      </div>
    </ModalDialog>
  );
};

export default GDPRPopupModal;
